import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDashboardLayout } from '../hooks/useDashboardLayout';
import { logger } from '../utils/logger';
import { Home, Calendar, Users, FileText, Settings, Stethoscope, User as UserIcon, BarChart3, Video, ShieldCheck, Bell, Activity, Lock, Database, Building2 } from 'lucide-react';
// ReconnectionHandler supprimé car géré globablement par SocketContext
import { ErrorBoundary } from '../components/ErrorBoundary';
import IncomingCallNotification from '../components/IncomingCallNotification';
import { DashboardSidebar, type NavItem } from './DashboardLayout/DashboardSidebar';
import { DashboardTopBar } from './DashboardLayout/DashboardTopBar';
import { BottomNav } from './DashboardLayout/BottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import HealthOnboarding from '../components/onboarding/HealthOnboarding';
import { useTranslation } from 'react-i18next';

interface DashboardLayoutProps {
  userType: 'patient' | 'doctor' | 'admin' | 'nurse' | 'clinic';
}

/**
 * Refactored DashboardLayout using composition
 * Orchestrates sidebar, top bar, and main content with shared state
 *
 * Architecture:
 * - DashboardSidebar: Navigation sidebar with logo and nav items
 * - DashboardTopBar: Top navigation with notifications and user menu
 * - useDashboardLayout: Custom hook managing all state and handlers
 * - Main outlet for page content
 */
const DashboardLayout: React.FC<DashboardLayoutProps> = ({ userType }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout, updateProfile } = useAuth();
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const onboardingInProgressRef = React.useRef(false);

  useEffect(() => {
    if (isAuthenticated && user && userType !== 'admin') {
      const justRegistered = sessionStorage.getItem('justRegistered') === 'true';
      const onboardingCompleted = (user.preferences as Record<string, unknown>)?.onboardingCompleted === true;
      
      if (justRegistered && !onboardingCompleted && !onboardingInProgressRef.current) {
        setShowOnboarding(true);
      }
    }
  }, [isAuthenticated, user, userType]);

  const handleOnboardingComplete = async (data: Record<string, unknown>) => {
    if (onboardingInProgressRef.current) return;
    
    try {
      onboardingInProgressRef.current = true;
      logger.info('[DashboardLayout] Finalisation de l\'onboarding...');
      
      sessionStorage.removeItem('justRegistered');
      
      const updates = {
        phoneNumber: data.phoneNumber as string,
        country: data.country as string,
        region: data.region as string,
        city: data.city as string,
        gender: data.gender as string,
        dateOfBirth: data.dateOfBirth as string,
        bloodType: data.bloodGroup as string,
        speciality: data.specialty as string,
        licenseNumber: data.licenseNumber as string,
        emergencyContact: data.emergencyContact as Record<string, unknown>,
        medicalHistory: data.allergies ? [`Allergies: ${data.allergies}`] : undefined,
      };

      await updateProfile(updates);
      
      try {
        const { updatePreferences } = await import('../services/profileService');
        const prefResponse = await updatePreferences({
          onboardingCompleted: true,
          onboardingDate: new Date().toISOString()
        });

        if (prefResponse && prefResponse.status === 'success') {
          const { useAuthStore } = await import('../store/authStore');
          const { StorageUtils } = await import('../utils/StorageUtils');
          const currentStoreUser = useAuthStore.getState().user;
          if (currentStoreUser) {
            const updatedUser = {
              ...currentStoreUser,
              preferences: {
                ...(currentStoreUser.preferences as Record<string, unknown> || {}),
                onboardingCompleted: true,
                onboardingDate: new Date().toISOString()
              }
            };
            useAuthStore.getState().setUser(updatedUser);
            StorageUtils.setUser(updatedUser);
          }
        }
      } catch (prefError) {
        logger.error('Failed to update onboarding preferences', prefError);
      }
      
      setShowOnboarding(false);
      onboardingInProgressRef.current = false;
      
      logger.info('Onboarding completed successfully');
    } catch (error) {
      logger.error('Failed to save onboarding data', error);
      onboardingInProgressRef.current = false;
    }
  };
  const location = useLocation();
  const hasLoggedAccessRef = useRef(false);

  const {
    sidebarOpen,
    avatarMenuOpen,
    unreadCount,
    unreadNotificationsCount,
    onlineUsersCount,
    incomingCall,
    toggleSidebar,
    closeSidebar,
    toggleAvatarMenu,
    closeAvatarMenu,
    acceptCall,
    declineCall,
    avatarMenuRef,
  } = useDashboardLayout();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      logger.auth('Non authentifié, préparation redirection serveur');
      return;
    }

    const expectedRole = userType.toLowerCase();
    const userRole = user.role.toLowerCase();
    const isAdminVariant = ['admin', 'saas_admin', 'super_admin'].includes(userRole);
    const isClinicVariant = ['clinic_manager', 'clinic'].includes(userRole);
    const isClinicStaff = ['nurse', 'doctor', 'clinic_nurse', 'clinic_doctor', 'receptionist'].includes(userRole) &&
      (Boolean(localStorage.getItem('activeClinicId')) || Boolean((user as { clinicMemberships?: unknown[] }).clinicMemberships?.length));
    // ✅ SÉCURITÉ: L'admin peut voir /clinic (supervision), clinic_manager et personnel clinique peuvent voir /clinic
    const isMatchingRole = userRole === expectedRole || 
      (expectedRole === 'admin' && isAdminVariant) ||
      (expectedRole === 'clinic' && (isClinicVariant || isAdminVariant || isClinicStaff));

    if (!isMatchingRole) {
      logger.auth(`Rôle incorrect: ${userRole} ≠ ${expectedRole}`);
      return;
    }
  }, [isAuthenticated, user, userType]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const expectedRole = userType.toLowerCase();
  const userRole = user?.role?.toLowerCase() || '';
  const isAdminVariant = ['admin', 'saas_admin', 'super_admin'].includes(userRole);
  const isClinicVariant = ['clinic_manager', 'clinic'].includes(userRole);
  const isClinicStaff = ['nurse', 'doctor', 'clinic_nurse', 'clinic_doctor', 'receptionist'].includes(userRole) &&
    (Boolean(localStorage.getItem('activeClinicId')) || Boolean((user as { clinicMemberships?: unknown[] }).clinicMemberships?.length));
  // ✅ SÉCURITÉ: Admin peut superviser /clinic, mais clinic_manager ne peut JAMAIS accéder à /admin
  const isMatchingRole = userRole === expectedRole || 
    (expectedRole === 'admin' && isAdminVariant) ||
    (expectedRole === 'clinic' && (isClinicVariant || isAdminVariant || isClinicStaff));

  if (!isMatchingRole) {
    // ✅ Mapping strict rôle → chemin d'accueil (évite "/clinic_manager" inexistant)
    const roleToPath: Record<string, string> = {
      'patient': '/patient',
      'doctor': '/doctor',
      'nurse': '/nurse',
      'clinic_manager': '/clinic',
      'clinic': '/clinic',
      'admin': '/admin',
      'saas_admin': '/admin',
      'super_admin': '/admin',
    };
    const redirectPath = roleToPath[userRole] || '/login';
    logger.warn(`[DashboardLayout] Rôle '${userRole}' redirigé vers '${redirectPath}' (layout attendu: '${expectedRole}')`);
    return <Navigate to={redirectPath} replace />;
  }

  const clinicRole = (localStorage.getItem('activeClinicRole') || '').toUpperCase();
  const isNurse = userRole === 'nurse' || clinicRole === 'CLINIC_NURSE';
  const isDoctor = userRole === 'doctor' || clinicRole === 'CLINIC_DOCTOR';
  const isReceptionist = clinicRole === 'RECEPTIONIST';

  // ✅ SÉCURITÉ: Une infirmière ou un médecin ne doit pas accéder au Command Center ni aux modules d'administration
  if (expectedRole === 'clinic' && (isNurse || isDoctor)) {
    const adminOnlyPaths = ['/clinic', '/clinic/workspaces', '/clinic/billing', '/clinic/practitioners', '/clinic/settings'];
    if (adminOnlyPaths.includes(location.pathname)) {
      return <Navigate to="/clinic/triage" replace />;
    }
  }

  if (!hasLoggedAccessRef.current) {
    logger.auth(`Accès autorisé pour ${userRole} sur ${expectedRole}`);
    hasLoggedAccessRef.current = true;
  }

  const getNavItems = (): NavItem[] => {
    switch (userType) {
      case 'patient':
        return [
          { name: t('navigation.sidebar.dashboard'), icon: <Home size={20} />, path: '/patient' },
          {
            name: t('navigation.sidebar.appointments'),
            icon: <Calendar size={20} />,
            path: '/patient/appointments',
          },
          {
            name: t('navigation.sidebar.medicalRecords'),
            icon: <FileText size={20} />,
            path: '/patient/medical-records',
          },
          { name: t('navigation.sidebar.messages'), icon: <UserIcon size={20} />, path: '/patient/messages' },
          { name: t('navigation.sidebar.doctors'), icon: <Users size={20} />, path: '/patient/doctors' },
          { name: t('navigation.sidebar.settings'), icon: <Settings size={20} />, path: '/patient/settings' },
        ];
      case 'doctor': {
        const hasClinicAccess = Boolean(localStorage.getItem('activeClinicId')) || 
          Boolean((user as { clinicMemberships?: unknown[] })?.clinicMemberships?.length);

        const items: NavItem[] = [
          { name: t('navigation.sidebar.dashboard'), icon: <Home size={20} />, path: '/doctor' },
        ];

        if (hasClinicAccess) {
          items.push({
            name: 'Ma Clinique (Triage)',
            icon: <Building2 size={20} />,
            path: '/clinic/triage',
          });
        }

        items.push(
          {
            name: t('navigation.sidebar.appointments'),
            icon: <Calendar size={20} />,
            path: '/doctor/appointments',
          },
          { name: t('navigation.sidebar.messages'), icon: <UserIcon size={20} />, path: '/doctor/messages' },
          {
            name: t('navigation.sidebar.patientRecords'),
            icon: <FileText size={20} />,
            path: '/doctor/patient-records/list',
          },
          { name: t('navigation.sidebar.colleagues'), icon: <Users size={20} />, path: '/doctor/directory' },
          { name: t('navigation.sidebar.settings'), icon: <Settings size={20} />, path: '/doctor/settings' },
        );

        return items;
      }
      case 'nurse':
        return [
          { name: 'Tableau de bord', icon: <Home size={20} />, path: '/nurse' },
          { name: 'File de Triage', icon: <Activity size={20} />, path: '/nurse/triage' },
          { name: 'Dossiers Patients', icon: <FileText size={20} />, path: '/nurse/records' },
          { name: 'Paramètres', icon: <Settings size={20} />, path: '/nurse/settings' },
        ];
      case 'clinic': {
        // ✅ Adaptation dynamique des menus selon le profil clinique
        if (isNurse || isDoctor) {
          const staffNav: NavItem[] = [
            { name: 'File & Triage', icon: <Activity size={20} />, path: '/clinic/triage' },
            { name: 'Orientations & Referrals', icon: <FileText size={20} />, path: '/clinic/referrals' },
          ];

          if (isDoctor || userRole === 'doctor') {
            staffNav.push({
              name: 'Espace Téléconsultations',
              icon: <Home size={20} />,
              path: '/doctor',
            });
          }

          return staffNav;
        }

        if (isReceptionist) {
          return [
            { name: 'File & Triage', icon: <Activity size={20} />, path: '/clinic/triage' },
            { name: 'Facturation & Caisse', icon: <BarChart3 size={20} />, path: '/clinic/billing' },
            { name: 'Orientations & Referrals', icon: <FileText size={20} />, path: '/clinic/referrals' },
          ];
        }

        // Par défaut pour CLINIC_ADMIN, CLINIC_OWNER, SAAS_ADMIN, ADMIN
        const adminClinicNav: NavItem[] = [
          { name: 'Command Center', icon: <Home size={20} />, path: '/clinic' },
          { name: 'Espaces & Cliniques', icon: <Building2 size={20} />, path: '/clinic/workspaces' },
          { name: 'File & Triage', icon: <Activity size={20} />, path: '/clinic/triage' },
          { name: 'Facturation & Caisse', icon: <BarChart3 size={20} />, path: '/clinic/billing' },
          { name: 'Orientations & Referrals', icon: <FileText size={20} />, path: '/clinic/referrals' },
          { name: 'Praticiens & Équipe', icon: <Users size={20} />, path: '/clinic/practitioners' },
          { name: 'Paramètres Structure', icon: <Settings size={20} />, path: '/clinic/settings' },
        ];

        if (isDoctor || userRole === 'doctor') {
          adminClinicNav.push({
            name: 'Espace Téléconsultations',
            icon: <Home size={20} />,
            path: '/doctor',
          });
        }

        return adminClinicNav;
      }
      case 'admin':
        return [
          { name: t('navigation.sidebar.dashboard'), icon: <Home size={20} />, path: '/admin' },
          { name: 'Portail Clinique', icon: <Activity size={20} />, path: '/clinic' },
          { name: t('navigation.sidebar.users'), icon: <Users size={20} />, path: '/admin/users' },
          { name: t('navigation.sidebar.doctors'), icon: <Stethoscope size={20} />, path: '/admin/doctors' },
          { name: t('navigation.sidebar.services'), icon: <Activity size={20} />, path: '/admin/services' },
          { name: t('navigation.sidebar.financials'), icon: <BarChart3 size={20} />, path: '/admin/financials' },
          { name: t('navigation.sidebar.consultations'), icon: <Video size={20} />, path: '/admin/consultations' },
          { name: t('navigation.sidebar.prescriptions'), icon: <FileText size={20} />, path: '/admin/prescriptions' },
          { name: t('navigation.sidebar.moderation'), icon: <ShieldCheck size={20} />, path: '/admin/moderation' },
          { name: t('navigation.sidebar.monitoring'), icon: <Database size={20} />, path: '/admin/monitoring' },
          { name: t('navigation.sidebar.communications'), icon: <Bell size={20} />, path: '/admin/communications' },
          { name: t('navigation.sidebar.permissions'), icon: <Lock size={20} />, path: '/admin/permissions' },
          { name: t('navigation.sidebar.dataExport'), icon: <FileText size={20} />, path: '/admin/data-export' },
          { name: t('navigation.sidebar.security'), icon: <ShieldCheck size={20} />, path: '/admin/security' },
          { name: t('navigation.sidebar.settings'), icon: <Settings size={20} />, path: '/admin/settings' },
        ];
      default:
        return [];
    }
  };

  // Generate page title based on current path
  const getPageTitle = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return t('navigation.sidebar.dashboard');

    const lastSegment = pathSegments[pathSegments.length - 1];

    // Detect if last segment is a UUID or a numeric ID (e.g. 27d8cf37-a60f-4160-bad9-d8e5ab1e3dae)
    const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment) || /^\d+$/.test(lastSegment);

    const targetSegment = isId && pathSegments.length > 1
      ? pathSegments[pathSegments.length - 2]
      : lastSegment;

    if (targetSegment === userType || targetSegment === '') return t('navigation.sidebar.dashboard');

    const sidebarKey = targetSegment.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const camelKey = targetSegment.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

    const translated = t(`navigation.sidebar.${sidebarKey}`);
    if (translated !== `navigation.sidebar.${sidebarKey}`) return translated;

    const translatedCamel = t(`navigation.sidebar.${camelKey}`);
    if (translatedCamel !== `navigation.sidebar.${camelKey}`) return translatedCamel;

    const routeTitles: Record<string, string> = {
      waiting_room: t('teleconsultation.waitingRoom.patient.roomTitle', { defaultValue: "Salle d'Attente" }),
      'waiting-room': t('teleconsultation.waitingRoom.patient.roomTitle', { defaultValue: "Salle d'Attente" }),
      consultation: t('teleconsultation.title', { defaultValue: 'Téléconsultation' }),
      appointments: t('navigation.sidebar.appointments', { defaultValue: 'Rendez-vous' }),
      medical_records: t('navigation.sidebar.medicalRecords', { defaultValue: 'Dossiers Médicaux' }),
      'medical-records': t('navigation.sidebar.medicalRecords', { defaultValue: 'Dossiers Médicaux' }),
      triage: t('clinic.queue.title', { defaultValue: 'File & Triage' }),
      queue: t('clinic.queue.title', { defaultValue: "File d'Attente" }),
      workspaces: t('clinic.workspaces.title', { defaultValue: 'Espaces Cliniques' }),
      billing: t('clinic.billing.title', { defaultValue: 'Facturation & Caisse' }),
      referrals: t('clinic.referrals.title', { defaultValue: 'Orientations & Referrals' }),
      practitioners: t('clinic.practitioners.title', { defaultValue: 'Praticiens & Équipe' }),
      clinic: t('clinic.title', { defaultValue: 'Portail Clinique' }),
    };

    if (routeTitles[targetSegment]) {
      return routeTitles[targetSegment];
    }

    return targetSegment.charAt(0).toUpperCase() + targetSegment.slice(1).replace(/[-_]/g, ' ');
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:flex-row font-outfit relative lg:overflow-hidden">
      {/* Background Decorations Elite */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-primary-500/5 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-pulse [animation-delay:2s]"></div>
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row w-full lg:h-screen lg:overflow-hidden">
      {showOnboarding && user && (
        <HealthOnboarding user={user!} onComplete={handleOnboardingComplete} />
      )}
      {/* Sidebar */}
      <DashboardSidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        navItems={navItems}
        userType={userType}
        user={user}
        onLogout={logout}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        <DashboardTopBar
          pageTitle={getPageTitle()}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
          unreadCount={unreadCount}
          unreadNotificationsCount={unreadNotificationsCount}
          onlineUsersCount={onlineUsersCount}
          userType={userType}
          avatarMenuOpen={avatarMenuOpen}
          onToggleAvatarMenu={toggleAvatarMenu}
          onCloseAvatarMenu={closeAvatarMenu}
          avatarMenuRef={avatarMenuRef}
        />

        {/* Page content - Reduced padding for Lite style */}
        <main className="flex-1 lg:overflow-y-auto p-4 sm:p-6 lg:p-6 bg-transparent pb-32 lg:pb-12">
          <ErrorBoundary>
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </ErrorBoundary>
        </main>

        {/* Bottom Navigation for mobile */}
        <BottomNav userType={userType} unreadCount={unreadCount} />

        {/* Incoming call notification for doctors */}
        {incomingCall && userType === 'doctor' && (
          <IncomingCallNotification
            callerName={incomingCall.requesterName}
            onAccept={acceptCall}
            onDecline={declineCall}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
